import errorReporter from '@app/errorReporter';
import type { AssetFolderDoc } from '@bb/domain/dist/schemas';
import { Col, Color, Flex, Row } from '@bb/elements-web';
import { useQuery } from '@tanstack/react-query';
import { getFolders } from 'api/AssetController';
import type { AuthContextUser } from 'contexts/types';
import { orderBy } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import AssetExplorer from './AssetExplorer';
import AssetsFolderList from './AssetsFolderList';
import AssetsPageHeader from './AssetsPageHeader';
import FolderModal from './Modals/FolderModal';
import { ROOT_FOLDER_PAGE_LIMIT } from './constants';

interface AssetsPageProps extends RouteComponentProps {
  auth: AuthContextUser;
}

const AssetsPage: React.FC<AssetsPageProps> = ({ auth, location, history }) => {
  const searchParams = new URLSearchParams(location.search);
  const folderId = searchParams.get('folderId');
  const isAdmin = Boolean(auth?.isAdmin);
  const limit = ROOT_FOLDER_PAGE_LIMIT;

  // pagination states
  const [currFolderPage, setCurrFolderPage] = useState(1);
  const [currAdminFolderPage, setCurrAdminFolderPage] = useState(1);
  const [currAssetsPage, setCurrAssetsPage] = useState(1);

  // folder modal
  const [isFolderModalVisible, setIsFolderModalVisible] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<
    AssetFolderDoc | undefined
  >(undefined);

  // used to keep track of selected folder on the left side
  const [rootFolderId, setRootFolderId] = useState<string | null>(null);

  useEffect(() => {
    if (!folderId) {
      setRootFolderId(null);
    }
  }, [folderId]);

  const { data: folders, isFetching: isFetchingFolders } = useQuery({
    queryKey: ['folders', 'root', currFolderPage, false],
    queryFn: () =>
      getFolders({
        limit,
        page: currFolderPage,
        isAdmin: false,
      }),
    select: data => ({
      assets: orderBy(
        data?.assets || [],
        [asset => asset.name.toLowerCase()],
        ['asc'],
      ),
      totalAssets: data?.totalAssets || 0,
    }),
    onError: fetchError => {
      errorReporter.error('Failed to load asset folders:', fetchError);
    },
    keepPreviousData: true,
  });

  const { data: adminFolders, isFetching: isFetchingAdminFolders } = useQuery({
    queryKey: ['folders', 'root', currAdminFolderPage, true],
    queryFn: () =>
      getFolders({
        limit,
        page: currAdminFolderPage,
        isAdmin: true,
      }),
    select: data => ({
      assets: orderBy(
        data?.assets || [],
        [asset => asset.name.toLowerCase()],
        ['asc'],
      ),
      totalAssets: data?.totalAssets || 0,
    }),
    onError: fetchError => {
      errorReporter.error('Failed to load asset folders:', fetchError);
    },
    keepPreviousData: true,
    enabled: isAdmin,
  });

  const handleRootFolderClick = useCallback(
    (folder: AssetFolderDoc) => {
      if (folderId === folder.id) return;
      setRootFolderId(folder.id);
      history.push(`/assets/?folderId=${folder.id}`);
      setCurrAssetsPage(1);
    },
    // excluding history because it's stable
    [folderId],
  );

  const handleSettingsClick = useCallback((folder: AssetFolderDoc) => {
    setSelectedFolder(folder);
    setIsFolderModalVisible(true);
  }, []);

  const handlePageChange = useCallback(
    (page: number) => setCurrAssetsPage(page),
    [],
  );

  const handleFolderPageChange = useCallback((page: number) => {
    setCurrFolderPage(page);
  }, []);

  const handleAdminFolderPageChange = useCallback((page: number) => {
    setCurrAdminFolderPage(page);
  }, []);

  return (
    <StyledRootContainer className="global--angular">
      <GlobalAssetStyles />
      <AssetsPageHeader
        folderId={folderId}
        onRootFolderIdUpdate={setRootFolderId}
      />
      <Row gutter={10}>
        <Col xs={{ span: 24 }} lg={{ span: 9 }}>
          <Flex vertical gap={5}>
            <AssetsFolderList
              title="BrokerBay Files"
              dataCy="brokerbay-files-list"
              assets={folders?.assets || []}
              totalAssets={folders?.totalAssets || 0}
              currPage={currFolderPage}
              pageSize={limit}
              onPageChange={handleFolderPageChange}
              loading={isFetchingFolders}
              actionsEnabled={isAdmin}
              folderId={rootFolderId}
              onFolderClick={handleRootFolderClick}
              onSettingsClick={handleSettingsClick}
            />
            {isAdmin && (
              <AssetsFolderList
                title="Admin Only Folders"
                dataCy="admin-only-folders-list"
                assets={adminFolders?.assets || []}
                totalAssets={adminFolders?.totalAssets || 0}
                currPage={currAdminFolderPage}
                pageSize={limit}
                onPageChange={handleAdminFolderPageChange}
                loading={isFetchingAdminFolders}
                actionsEnabled
                folderId={rootFolderId}
                onFolderClick={handleRootFolderClick}
                onSettingsClick={handleSettingsClick}
              />
            )}
          </Flex>
        </Col>
        <Col xs={{ span: 24 }} lg={{ span: 15 }}>
          <AssetExplorer
            isAdmin={isAdmin}
            folderId={folderId}
            currAssetsPage={currAssetsPage}
            onPageChange={handlePageChange}
            onFolderSettingsClick={handleSettingsClick}
          />
        </Col>
      </Row>
      <FolderModal
        folder={selectedFolder}
        visible={isFolderModalVisible}
        onClose={() => {
          setSelectedFolder(undefined);
          setIsFolderModalVisible(false);
        }}
      />
    </StyledRootContainer>
  );
};

export default AssetsPage;

const StyledRootContainer = styled.div`
  && {
    padding: 5px 5px 0;
  }
`;

const GlobalAssetStyles = createGlobalStyle`

  && {
      .ant-list, .ant5-list {
        .ant-list-item .ant-list-item-meta-description, 
        .ant5-list-item .ant5-list-item-meta-description {
          color: ${Color.TRUNKS};
        }

        .ant-list-item-meta-avatar .anticon > svg, 
        .ant5-list-item-meta-avatar .anticon > svg,
        .ant-list-item-action .anticon > svg, 
        .ant5-list-item-action .anticon > svg {
          color: ${Color.TRUNKS};
        }
      }

      .ant-list-pagination, 
      .ant5-list-pagination{
        margin-top: 6px;

        .ant-pagination,
        .ant5-pagination {
          padding: 0 16px;
          text-align: left;
        }
      }
  }

`;
